import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";
import { Icon } from "../atoms/icon.js";

const modalVariants = cva(
    "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg",
    {
        variants: {
            size: {
                sm: "max-w-md",
                md: "max-w-lg",
                lg: "max-w-2xl",
                xl: "max-w-4xl",
                full: "max-w-[95vw] max-h-[95vh]",
            },
        },
        defaultVariants: {
            size: "md",
        },
    }
);

export interface ModalProps
    extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
    VariantProps<typeof modalVariants> {
    title?: string;
    description?: string;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    showCloseButton?: boolean;
    className?: string;
    overlayClassName?: string;
}

export const Modal = React.forwardRef<
    React.ElementRef<typeof DialogPrimitive.Content>,
    ModalProps
>(({
    className,
    size,
    title,
    description,
    children,
    footer,
    showCloseButton = true,
    overlayClassName,
    ...props
}, ref) => (
    <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
            className={cn(
                "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                overlayClassName
            )}
        />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(modalVariants({ size }), className)}
            {...props}
        >
            {(title || description || showCloseButton) && (
                <div className="flex flex-col space-y-1.5 text-center sm:text-left">
                    {title && (
                        <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                            {title}
                        </DialogPrimitive.Title>
                    )}
                    {description && (
                        <DialogPrimitive.Description className="text-sm text-muted-foreground">
                            {description}
                        </DialogPrimitive.Description>
                    )}
                </div>
            )}

            {children && (
                <div className="flex-1 overflow-auto">
                    {children}
                </div>
            )}

            {footer && (
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                    {footer}
                </div>
            )}

            {showCloseButton && (
                <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
                    <Icon name="close" size="sm" />
                    <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
            )}
        </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
));

Modal.displayName = "Modal";

// Export Radix primitives for advanced usage
export const ModalTrigger = DialogPrimitive.Trigger;
export const ModalClose = DialogPrimitive.Close;
export const ModalRoot = DialogPrimitive.Root;

// Convenience wrapper component
export interface SimpleModalProps extends Omit<ModalProps, 'open' | 'onOpenChange'> {
    isOpen: boolean;
    onClose: () => void;
    trigger?: React.ReactNode;
}

export const SimpleModal: React.FC<SimpleModalProps> = ({
    isOpen,
    onClose,
    trigger,
    children,
    ...props
}) => {
    return (
        <ModalRoot open={isOpen} onOpenChange={(open) => !open && onClose()}>
            {trigger && <ModalTrigger asChild>{trigger}</ModalTrigger>}
            <Modal {...props}>
                {children}
            </Modal>
        </ModalRoot>
    );
};
